/**
 * Hubwayz / Mishor hotels — FE calls BE proxy only (no secrets in browser).
 */

import axios from "axios";
import { ApiReqBaseUrl } from "../assets/GlobalVariables";
import { getSessionId, getUserDetails } from "./authService";
import {
  denyHubwayzSupplierBookExecution,
  denyHubwayzSupplierCancelExecution,
  isHubwayzBookingDetailsNetworkAllowed,
  isHubwayzSupplierBookDispatchPermitted,
  isHubwayzSupplierCancelExecutionAllowed,
} from "../features/unifiedSearchMockup/hubwayz/hubwayzBookExecutionGuard";
import {
  HUBWAYZ_UI_TEST_BOOK_HEADER,
} from "../features/unifiedSearchMockup/hubwayz/hubwayzUiTestHotelsBookUnlock";
import {
  buildHubwayzCancelRequest,
  normalizeHubwayzCancel,
} from "../features/unifiedSearchMockup/hubwayz/normalizeHubwayzCancel";
import { normalizeHubwayzBookingDetails } from "../features/unifiedSearchMockup/hubwayz/normalizeHubwayzBookingDetails";
import { normalizeHubwayzBook } from "../features/unifiedSearchMockup/hubwayz/normalizeHubwayzBook";
import { HUBWAYZ_CERT_TEST_VOUCHER_EMAIL } from "../features/unifiedSearchMockup/hubwayz/hubwayzCertVoucher";
import { hideSupplierBrandFromUserText } from "../features/unifiedSearchMockup/hideSupplierBrandFromUserText";
import {
  beginHubwayzSessionLogAttempt,
  currentHubwayzSessionLogAttempt,
  hubwayzAttemptHeaders,
} from "../features/unifiedSearchMockup/hubwayz/hubwayzSessionLogAttempt";
import {
  buildHubwayzCustomerPriceRequest,
  buildHubwayzQuoteRequest,
} from "../features/unifiedSearchMockup/hubwayz/hubwayzVerifiedQuote";

const base = () => {
  const root = String(ApiReqBaseUrl || "").replace(/\/$/, "");
  if (root === "/api" || root.endsWith("/api")) return `${root}/hubwayz`;
  return `${root}/api/hubwayz`;
};

async function hubwayzAgentSessionHeaders() {
  const sessionRaw = await getSessionId();
  const sessionID =
    sessionRaw && typeof sessionRaw === "string" && sessionRaw !== "false"
      ? sessionRaw
      : null;
  if (!sessionID) return {};
  return {
    "X-Session-ID": String(sessionID),
    "X-Session-Id": String(sessionID),
  };
}

export async function fetchHubwayzStatus() {
  const { data } = await axios.get(`${base()}/status`);
  return data;
}

export async function fetchHubwayzDestinations(countryCode) {
  const { data } = await axios.get(
    `${base()}/destinations/${encodeURIComponent(String(countryCode || "").toUpperCase())}`
  );
  return data;
}

const destinationCatalogCache = new Map();
const DESTINATION_CATALOG_CACHE_MS = 20000;

/**
 * Autocomplete against the HotelsRiver destination catalog.
 * The browser never calls Hubwayz and never sends supplier credentials.
 */
export async function searchHubwayzDestinationCatalog(query) {
  const q = String(query || "").trim().slice(0, 64);
  if (q.length < 2) return { ok: true, results: [] };
  const cached = destinationCatalogCache.get(q);
  if (cached && Date.now() - cached.at < DESTINATION_CATALOG_CACHE_MS) {
    return cached.data;
  }
  const { data } = await axios.get(`${base()}/destinations/search`, {
    params: { q },
    timeout: 8000,
  });
  destinationCatalogCache.set(q, { at: Date.now(), data });
  return data;
}

/**
 * @param {{
 *   destinationId: number|string,
 *   destinationType?: 'location'|'hotel',
 *   checkIn: string,
 *   checkOut: string,
 *   rooms?: Array<{ adults: number, childAges?: number[] }>,
 *   waitUntilDone?: boolean,
 *   enrichStatic?: boolean,
 * }} params
 */
export async function searchHubwayzHotels(params) {
  const attemptId = beginHubwayzSessionLogAttempt();
  const { data } = await axios.post(`${base()}/hotels/search`, {
    destinationId: params.destinationId,
    destinationType: params.destinationType || "location",
    countryCode: params.countryCode,
    checkIn: params.checkIn,
    checkOut: params.checkOut,
    rooms: params.rooms || [{ adults: 2 }],
    customerCountry: params.customerCountry || "IL",
    currencies: params.currencies || ["USD"],
    waitUntilDone: params.waitUntilDone !== false,
    enrichStatic: params.enrichStatic !== false,
    attemptId,
  }, {
    headers: {
      ...hubwayzAttemptHeaders(),
      ...(await hubwayzAgentSessionHeaders()),
      "Cache-Control": "no-store",
    },
  });
  return data;
}

/**
 * Verified Hubwayz price. The body is an allow-list: offer identity only.
 * Supplier price, FX, HR margin, and agent commission are not sent.
 */
export async function quoteHubwayzHotel(input = {}) {
  const { data } = await axios.post(`${base()}/hotels/quote`, buildHubwayzQuoteRequest(input), {
    headers: {
      ...hubwayzAttemptHeaders(),
      ...(await hubwayzAgentSessionHeaders()),
      "Cache-Control": "no-store",
    },
  });
  return data;
}

/**
 * Recompute one offer with a commission type and value.
 * The browser does not send the customer total.
 */
export async function quoteHubwayzCustomerPrice(input = {}) {
  const body = buildHubwayzCustomerPriceRequest(input);
  if (!body) {
    return { ok: false, binding: false, status: "commission_invalid" };
  }
  const { data } = await axios.post(`${base()}/hotels/quote/customer-price`, body, {
    headers: {
      ...hubwayzAttemptHeaders(),
      ...(await hubwayzAgentSessionHeaders()),
      "Cache-Control": "no-store",
    },
  });
  return data;
}

/**
 * Extract a readable Hubwayz / BE error (axios wraps 4xx as generic message).
 * @param {unknown} err
 * @returns {string}
 */
export function formatHubwayzApiError(err) {
  const data = err?.response?.data;
  if (data && typeof data === "object") {
    const upstream = data.upstream;
    const upstreamMsg =
      (upstream && typeof upstream === "object"
        ? upstream.errorMessage || upstream.message || upstream.error
        : null) ||
      (typeof upstream === "string" ? upstream : null);
    const code =
      upstream && typeof upstream === "object" ? upstream.errorCode : null;
    const baseMsg = data.error || data.message || null;
    if (upstreamMsg && code) {
      return hideSupplierBrandFromUserText(`${upstreamMsg} (${code})`, "הבקשה נכשלה");
    }
    if (upstreamMsg) {
      return hideSupplierBrandFromUserText(upstreamMsg, "הבקשה נכשלה");
    }
    if (baseMsg) return hideSupplierBrandFromUserText(baseMsg, "הבקשה נכשלה");
    const status = String(data.status || "");
    if (status === "offer_not_found") {
      return "ההצעה לא נמצאה. חזרו לתוצאות וחפשו מחדש.";
    }
    if (status === "offer_expired") {
      return "תוקף ההצעה פג. חפשו מחדש.";
    }
    if (status === "offer_mismatch" || status === "quote_missing") {
      return "פרטי ההצעה לא תואמים את המחיר שאומת. חפשו מחדש.";
    }
    if (status === "unauthorized") return "נדרשת התחברות מחדש.";
    if (status) return "לא ניתן לאשר את המחיר מול הספק.";
  }
  if (err?.message) return hideSupplierBrandFromUserText(err.message, "הבקשה נכשלה");
  return hideSupplierBrandFromUserText(err, "הבקשה נכשלה");
}

/**
 * @param {{
 *   searchRequest: object,
 *   code?: string,
 *   codes?: Array<{ code: string, pax?: object[] }>,
 *   rooms?: Array<{ adults: number, childAges?: number[] }>,
 * }} params
 */
export async function prebookHubwayzHotel(params) {
  const searchRequest = params.searchRequest;
  // Prefer original Mishor search pax — rebuilt rooms often mismatch and yield a414.
  const codes =
    params.codes ||
    (params.code
      ? [
          {
            code: params.code,
            pax: Array.isArray(searchRequest?.pax)
              ? searchRequest.pax
              : undefined,
          },
        ]
      : undefined);

  try {
    const { data } = await axios.post(`${base()}/hotels/prebook`, {
      attemptId: currentHubwayzSessionLogAttempt(),
      searchId: params.searchId || undefined,
      searchRequest,
      code: codes ? undefined : params.code,
      codes,
      rooms: codes ? undefined : params.rooms,
    }, {
      headers: {
        ...hubwayzAttemptHeaders(),
        ...(await hubwayzAgentSessionHeaders()),
      },
    });
    return data;
  } catch (err) {
    const message = formatHubwayzApiError(err);
    const status = err?.response?.status;
    const data = err?.response?.data;
    return {
      ok: false,
      error: message,
      status,
      upstream: data?.upstream ?? null,
    };
  }
}

/**
 * Pass-through Mishor book body (server-side credentials).
 * Dual-gate hard-block remains default. Scoped overrides:
 * - certOneAttemptAuthorized (Phase 2N)
 * - uiTestHotelsOnlyAuthorized (Phase 2P owner-authorized TEST UI)
 * Does NOT flip global dual gates.
 *
 * @param {object} payload
 * @param {{ certOneAttemptAuthorized?: boolean, uiTestHotelsOnlyAuthorized?: boolean }} [opts]
 */
export async function bookHubwayzHotel(payload, opts = {}) {
  void opts;
  const dualOk = isHubwayzSupplierBookDispatchPermitted();
  const certScoped = false;
  const uiTestScoped = false;
  if (!dualOk && !certScoped && !uiTestScoped) {
    const denial = denyHubwayzSupplierBookExecution(payload);
    return {
      ok: false,
      dryRunBlocked: true,
      dryRun: true,
      networkCalled: false,
      error: denial.error.message,
      code: denial.error.code,
      upstream: null,
    };
  }
  try {
    const sessionRaw = await getSessionId();
    const sessionID =
      sessionRaw && typeof sessionRaw === "string" && sessionRaw !== "false"
        ? sessionRaw
        : null;
    const headers = {
      Accept: "application/json",
      ...hubwayzAttemptHeaders(),
      ...(sessionID
        ? { "X-Session-ID": String(sessionID), "X-Session-Id": String(sessionID) }
        : {}),
      ...(uiTestScoped ? { [HUBWAYZ_UI_TEST_BOOK_HEADER]: "1" } : {}),
      ...(certScoped ? { "X-Hubwayz-Cert-One-Attempt": "1" } : {}),
    };
    const { data } = await axios.post(
      `${base()}/hotels/book`,
      { attemptId: currentHubwayzSessionLogAttempt(), payload },
      {
        headers,
        withCredentials: true,
      }
    );
    const normalized = normalizeHubwayzBook(data);
    return {
      ...data,
      ok: data?.ok !== false && normalized.hotelServiceConfirmed,
      networkCalled: true,
      dryRun: false,
      normalized,
      bookingID: normalized.bookingID,
    };
  } catch (err) {
    const message = formatHubwayzApiError(err);
    const status = err?.response?.status;
    const data = err?.response?.data;
    return {
      ok: false,
      error: message,
      status,
      upstream: data?.upstream ?? data ?? null,
      networkCalled: true,
      dryRun: false,
    };
  }
}

/**
 * Save the unpaid internal Hubwayz order. Never calls supplier Book.
 */
export async function saveHubwayzPendingPaymentOrder(body = {}) {
  const { data } = await axios.post(
    `${base()}/hotels/pending-payment-order`,
    body,
    {
      withCredentials: true,
      headers: {
        Accept: "application/json",
        ...(await hubwayzAgentSessionHeaders()),
      },
    }
  );
  return data;
}

export async function persistHubwayzHotelOrder(body = {}) {
  try {
    const sessionRaw = await getSessionId();
    const sessionID =
      sessionRaw && typeof sessionRaw === "string" && sessionRaw !== "false"
        ? sessionRaw
        : null;
    const user = getUserDetails?.() || null;
    const storedAgentId =
      typeof localStorage !== "undefined"
        ? localStorage.getItem("agentId")
        : null;
    const agentId =
      user?.id ??
      user?.agentId ??
      user?.agent_id ??
      user?.agentid ??
      (storedAgentId && storedAgentId !== "false" ? storedAgentId : null);
    const { data } = await axios.post(
      `${base()}/hotels/persist-order`,
      {
        ...body,
        agentId: body.agentId ?? agentId,
        voucherEmail: body.voucherEmail || HUBWAYZ_CERT_TEST_VOUCHER_EMAIL,
        paymentMethod: body.paymentMethod || "account_credit",
      },
      {
        withCredentials: true,
        headers: {
          Accept: "application/json",
          ...(sessionID
            ? {
                "X-Session-ID": String(sessionID),
                "X-Session-Id": String(sessionID),
              }
            : {}),
          [HUBWAYZ_UI_TEST_BOOK_HEADER]: "1",
        },
      }
    );
    return data;
  } catch (err) {
    return {
      ok: false,
      error: formatHubwayzApiError(err),
      status: err?.response?.status,
      upstream: err?.response?.data ?? null,
      code: err?.response?.data?.code || "persist_failed",
    };
  }
}

/**
 * Official: GET /booking-details/{booking-id} on booking host (read-only).
 * Phase 2L: network blocked by HUBWAYZ_BOOKING_DETAILS_NETWORK_ALLOWED (default false).
 * Reconciliation when bookingID missing: see planHubwayzBookingDetailsReconciliation.
 */
export async function fetchHubwayzBookingDetails(bookingId) {
  const id = String(bookingId || "").trim();
  if (!id) {
    return {
      ok: false,
      error: "missing_booking_id",
      code: "missing_booking_id",
      networkCalled: false,
      normalized: null,
      pathTemplate: "/booking-details/{booking-id}",
    };
  }
  if (!isHubwayzBookingDetailsNetworkAllowed()) {
    return {
      ok: false,
      dryRunBlocked: true,
      networkCalled: false,
      error: "booking_details_network_disabled",
      code: "booking_details_network_disabled",
      pathTemplate: "/booking-details/{booking-id}",
      bookingId: id,
      normalized: null,
    };
  }
  try {
    const { data } = await axios.get(
      `${base()}/hotels/booking-details/${encodeURIComponent(id)}`,
      {
        params: { attemptId: currentHubwayzSessionLogAttempt() },
        headers: hubwayzAttemptHeaders(),
      }
    );
    const normalized = normalizeHubwayzBookingDetails(data);
    return {
      ok: normalized.ok,
      networkCalled: true,
      data,
      normalized,
      bookingId: normalized.bookingId || id,
    };
  } catch (err) {
    return {
      ok: false,
      networkCalled: true,
      error: formatHubwayzApiError(err),
      status: err?.response?.status,
      upstream: err?.response?.data ?? null,
      normalized: null,
    };
  }
}

/**
 * Official: POST /booking-cancel (Force hard-locked false).
 * Phase 2L: hard Cancel gate OFF — validates + builds payload, never executes.
 *
 * @param {{
 *   bookingId: string|number,
 *   cancelReason?: string,
 *   force?: boolean,
 *   isManual?: boolean,
 * }} params
 */
export async function cancelHubwayzBooking(params = {}) {
  const built = buildHubwayzCancelRequest(params);
  if (!built.ok) {
    return {
      ok: false,
      networkCalled: false,
      error: built.error.message,
      code: built.error.code,
      payload: null,
      normalized: null,
    };
  }

  if (!isHubwayzSupplierCancelExecutionAllowed()) {
    const denial = denyHubwayzSupplierCancelExecution(built.payload);
    return {
      ok: false,
      dryRunBlocked: true,
      dryRun: true,
      networkCalled: false,
      error: denial.error.message,
      code: denial.error.code,
      payload: built.payload,
      force: false,
      normalized: null,
    };
  }

  try {
    const { data } = await axios.post(
      `${base()}/hotels/booking-cancel`,
      {
        ...built.payload,
        attemptId: currentHubwayzSessionLogAttempt(),
      },
      { headers: hubwayzAttemptHeaders() }
    );
    const normalized = normalizeHubwayzCancel(data);
    return {
      ok: normalized.ok,
      networkCalled: true,
      data,
      payload: built.payload,
      normalized,
    };
  } catch (err) {
    return {
      ok: false,
      networkCalled: true,
      error: formatHubwayzApiError(err),
      status: err?.response?.status,
      upstream: err?.response?.data ?? null,
      payload: built.payload,
      normalized: null,
    };
  }
}

/**
 * Orders V2 UI Cancel — dedicated BE path (session + ownership).
 * Bypasses Phase 2L cert Cancel gate (that gate is for sandbox Book cleanup only).
 * Force is always false. Does NOT call Gimmonix /cancelOrder or LDS.
 *
 * @param {{
 *   orderId: string|number,
 *   cancelReason?: string,
 * }} params
 */
export async function cancelHubwayzHotelOrderFromOrdersUi(params = {}) {
  const orderId = params.orderId;
  if (orderId == null || String(orderId).trim() === "") {
    return {
      ok: false,
      networkCalled: false,
      code: "missing_order_id",
      error: "חסר מזהה הזמנה לביטול",
      outcome: "rejected",
    };
  }

  if (params.force === true) {
    return {
      ok: false,
      networkCalled: false,
      code: "force_forbidden",
      error: "לא ניתן לבטל בכוח",
      outcome: "rejected",
      force: false,
    };
  }

  try {
    const sessionRaw = await getSessionId();
    const sessionID =
      sessionRaw && typeof sessionRaw === "string" && sessionRaw !== "false"
        ? sessionRaw
        : null;
    const headers = {
      Accept: "application/json",
      ...hubwayzAttemptHeaders(),
      ...(sessionID
        ? { "X-Session-ID": String(sessionID), "X-Session-Id": String(sessionID) }
        : {}),
    };
    const { data } = await axios.post(
      `${base()}/hotels/orders-cancel`,
      {
        orderID: orderId,
        CancelReason: String(params.cancelReason ?? "").trim(),
        Force: false,
        IsManual: false,
        attemptId: currentHubwayzSessionLogAttempt(),
      },
      { headers, withCredentials: true, timeout: 60000 }
    );

    const hr = data?.hr_cancel || {};
    const supplierStatus = String(data?.supplier_status || "").toLowerCase();
    const already = Boolean(data?.already_cancelled || data?.idempotent_hit);
    const syncPending = Boolean(hr.sync_pending);
    const unknown =
      hr.reason === "unknown_cancel_outcome_no_retry" ||
      data?.code === "cancel_outcome_unknown";
    const cancelledOk =
      data?.ok === true &&
      (already ||
        supplierStatus === "cancelled" ||
        supplierStatus === "canceled" ||
        hr.cancelled === true ||
        hr.ok === true);

    let outcome = "rejected";
    if (unknown) outcome = "unknown";
    else if (syncPending && (supplierStatus === "cancelled" || supplierStatus === "canceled")) {
      outcome = "supplier_ok_db_pending";
    } else if (cancelledOk) outcome = "cancelled";
    else if (data?.ok === false) outcome = "rejected";

    return {
      ok: outcome === "cancelled" || outcome === "supplier_ok_db_pending",
      networkCalled: true,
      outcome,
      data,
      bookingID: data?.bookingID ?? null,
      orderID: data?.orderID ?? orderId,
      cancelFee: data?.cancel_fee ?? data?.pre_cancel_fee ?? null,
      hrCancel: hr,
      force: false,
      lds: false,
      gimmonix: false,
      messageHe:
        outcome === "cancelled"
          ? "ההזמנה בוטלה בהצלחה"
          : outcome === "unknown" || outcome === "supplier_ok_db_pending"
            ? "סטטוס הביטול בבדיקה"
            : data?.message_he || data?.error || "ביטול ההזמנה נדחה",
    };
  } catch (err) {
    const status = err?.response?.status;
    const upstream = err?.response?.data ?? null;
    const code = upstream?.code || null;
    const isTimeout =
      err?.code === "ECONNABORTED" ||
      /timeout/i.test(String(err?.message || "")) ||
      code === "cancel_outcome_unknown" ||
      status === 504;
    return {
      ok: false,
      networkCalled: true,
      outcome: isTimeout ? "unknown" : "rejected",
      error: formatHubwayzApiError(err),
      code: code || (isTimeout ? "cancel_outcome_unknown" : "cancel_failed"),
      status,
      upstream,
      force: false,
      noRetry: true,
      messageHe: isTimeout
        ? "סטטוס הביטול בבדיקה"
        : upstream?.message_he || formatHubwayzApiError(err) || "ביטול ההזמנה נדחה",
    };
  }
}

/** Cancellation *policy* by offer code — NOT booking cancel. */
export async function fetchHubwayzCancellation({ code, searchRequest }) {
  const { data } = await axios.post(
    `${base()}/hotels/cancellation`,
    {
      attemptId: currentHubwayzSessionLogAttempt(),
      code,
      searchRequest,
    },
    { headers: hubwayzAttemptHeaders() }
  );
  return data;
}

const hotelDescriptionCache = new Map();

function hasHebrewScript(text) {
  return /[\u0590-\u05FF]/.test(String(text || ""));
}

/**
 * Server translation with an in-memory cache on the API process.
 * A successful Hebrew result is remembered here so a later visit in this
 * tab does not ask the server to translate the same text again.
 * Failure returns the English source and is not cached.
 */
export async function translateHubwayzHotelDescription(text) {
  const raw = String(text || "").trim();
  if (!raw) return { text: "", language: "en", cached: false };
  if (hasHebrewScript(raw)) return { text: raw, language: "he", cached: false };
  if (hotelDescriptionCache.has(raw)) {
    return { ...hotelDescriptionCache.get(raw), cached: true };
  }
  try {
    const { data } = await axios.post(
      `${base()}/hotels/description-he`,
      { text: raw },
      { timeout: 20000 }
    );
    const translated = String(data?.text || "").trim();
    if (data?.language === "he" && hasHebrewScript(translated)) {
      const result = {
        text: translated,
        language: "he",
        cached: Boolean(data?.cached),
      };
      hotelDescriptionCache.set(raw, result);
      return result;
    }
  } catch {
    /* supplier English stays on screen */
  }
  return { text: raw, language: "en", cached: false };
}
